import { computed, type ComputedRef } from 'vue';

import { useAccessStore } from '@/store/accessStore';
import { NON_VENDOR_SCOPED, PERMISSIONS } from '@/types/access';
import type { MeVendor } from '@/types/access';

export interface Access {
  hasPermission: (permission: string) => boolean;
  hasAllVendors: ComputedRef<boolean>;
  canSeeCustomerMappings: ComputedRef<boolean>;
  hasAnyAccess: ComputedRef<boolean>;
  canAccessVendor: (vendorId: string) => boolean;
  scopedVendors: ComputedRef<MeVendor[]>;
}

// Access is decided by the /me permission list and vendor scope only; the role name can be
// null for custom Auth0 roles and must never be gated on.
export function useAccess(): Access {
  const store = useAccessStore();

  const permissions = computed(() => store.me?.permissions ?? []);

  const hasPermission = (permission: string): boolean => permissions.value.includes(permission);

  const hasAllVendors = computed(() => store.me?.vendors === 'all');

  const canSeeCustomerMappings = computed(() => hasPermission(PERMISSIONS.READ_CUSTOMER_MAPPINGS));

  // Vendor-scoped permissions are useless with zero vendors in scope, so a leftover grant
  // without a vendor mapping still lands on "access required".
  const hasAnyAccess = computed(() => {
    if (!store.me || permissions.value.length === 0) return false;
    if (hasAllVendors.value || (Array.isArray(store.me.vendors) && store.me.vendors.length > 0)) return true;
    return permissions.value.some((permission) => NON_VENDOR_SCOPED.has(permission));
  });

  const canAccessVendor = (vendorId: string): boolean => {
    if (hasAllVendors.value) return true;
    return Array.isArray(store.me?.vendors) && store.me.vendors.some((vendor) => vendor.id === vendorId);
  };

  // Vendor pickers for a scoped caller resolve from their /me vendors; all-vendor (and
  // unloaded) pickers source the full directory via vendorStore, so [] is right here.
  const scopedVendors = computed<MeVendor[]>(() => {
    if (!store.me || store.me.vendors === 'all') return [];
    return store.me.vendors;
  });

  return { hasPermission, hasAllVendors, canSeeCustomerMappings, hasAnyAccess, canAccessVendor, scopedVendors };
}
